import React, {useEffect, useState} from "react";
import {ajaxErrorHandler, formatUTCDate} from "../../../../libs/utils";
import AdminSubHeader from "../../../layouts/partials/AdminSubheader";
import ListView from "../../../../base/ListView";
import {getCompanies} from "../../../../services/api/companies";
import {getCrewMembers} from "../../../../services/api/crewMembers";
import {selectEvent} from "../../../../redux/preferences/selectors";
import {useSelector} from "react-redux";
import {getParkings} from "../../../../services/api/parkings";
import {getParkingStickers} from "../../../../services/api/parkingVouchers";
import {Link} from "react-router-dom";
import {getActivity} from "../../../../services/api/activity";
import {getUsers} from "../../../../services/api/users";
import ActivityProperties from "./ActivityProperties";

const Activity = () => {
    const event = useSelector(selectEvent);
    const [activity, setActivity] = useState([]);
    const [activityMeta, setActivityMeta] = useState({});
    const [loading, setLoading] = useState(true);
    const [baseFilters, setBaseFilters] = useState({
        "page[number]": 1,
        "page[size]": 10,
    });

    useEffect(() => {
        loadActivity();
    }, [baseFilters]);

    const loadActivity = () => {
        getActivity(baseFilters)
            .then(response => {
                setActivity(response.data);
                setActivityMeta(response.meta);
            })
            .catch(ajaxErrorHandler)
            .then(() => {
                setLoading(false);
            });
    };

    const renderDescription = activity => {
        if (activity.subjectType.includes('CrewMemberAccess')) {
            if (activity.description === 'deleted') {
                return <React.Fragment>deleted access to zone (id: {activity.subjectId})</React.Fragment>;
            }

            return <React.Fragment>
                added <strong>{activity.subjectData.zone}</strong> ({activity.subjectData.zone_code}) access
                to <strong>{activity.subjectUserName}</strong> for {activity.subjectData.day}
            </React.Fragment>
        }

        if (activity.subjectType.match(/\\CrewMember$/)) {
            return <React.Fragment>
                {activity.description === 'created' ? 'added' : 'updated'}{' '}
                <strong>{activity.subjectUserName}</strong> in <strong>{activity.subjectData?.company || '[deleted]'}</strong>'s
                registration
            </React.Fragment>
        }

        if (activity.subjectType.includes('wristband')) {
            return <React.Fragment>
                {activity.description === 'created' ? 'printed' : activity.description} wristband <strong>{activity.subjectData.barcode}</strong>{' '}
                ({activity.subjectData.access_voucher}, zones: {activity.subjectData.zones}){' '}
                for <strong>{activity.subjectUserName}</strong>
            </React.Fragment>
        }

        if (activity.subjectType.includes('access_voucher')) {
            return <React.Fragment>
                {activity.description} access
                voucher <strong>{activity.subjectData.name}</strong> ({activity.subjectData.barcode}){' '}
                for <strong>{activity.subjectUserName}</strong>
            </React.Fragment>
        }

        if (activity.subjectType.match(/\\User$/)) {
            return <React.Fragment>
                {activity.description} user <strong>{activity.subjectUserName}</strong>
            </React.Fragment>
        }

        if (activity.subjectType.match(/\\Permission$/)) {
            return <React.Fragment>
                {activity.description} permission <strong>{activity.subjectData?.name}</strong>
            </React.Fragment>
        }

        if (activity.subjectType.includes('RegistrationProfileDay')) {
            return <React.Fragment>
                set <strong>{activity.subjectData.company}</strong> profile limit
                for <strong>{activity.subjectData.profile}</strong> to {activity.subjectData.amount} on {activity.subjectData.day}
            </React.Fragment>
        }

        if (activity.subjectType.match('\\ParkingRequest$')) {
            return <React.Fragment>
                {activity.description} parking request for{' '}
                <strong>{activity.subjectData?.parking}</strong>{' '}
                in <strong>{activity.subjectData?.company}</strong>'s registration
                <br/>
                (request for <strong>{activity.subjectData?.quantity} sticker(s)</strong> for{' '}
                <strong>{activity.subjectData?.days}</strong>)<br/>
            </React.Fragment>
        }

        if (activity.subjectType.match(/registration/)) {
            if (activity.description !== 'updated' && activity.description !== 'created') {
                return <React.Fragment>
                    {activity.description} for <strong>{activity.subjectData?.company || '[deleted]'}</strong>'s
                    registration
                </React.Fragment>

            }

            return <React.Fragment>
                {activity.description} <strong>{activity.subjectData?.company || '[deleted]'}</strong>'s
                registration
            </React.Fragment>
        }

        return `${activity.description} ${activity.subjectTypeString}`;
    };

    const subjects = [
        'access_voucher',
        'CateringOption',
        'CateringOptionClaim',
        'CateringOptionPeriod',
        'RegistrationCateringOption',
        'CrewMember',
        'CrewMemberAccess',
        'CrewMemberTransit',
        'Profile',
        'ProfileAccess',
        'ProfileGroup',
        'Event',
        'Period',
        'Venue',
        'Zone',
        'Gate',
        'Location',
        'TransportVoucherTemplate',
        'CrewMemberMaterial',
        'CrewMemberMaterialTemplate',
        'CrewMemberMaterialTemplateLimit',
        'EventMaterial',
        'Material',
        'MaterialCategory',
        'MaterialRental',
        'MaterialRequestDay',
        'Parking',
        'ParkingRequest',
        'ParkingRequestParkingSticker',
        'ParkingSticker',
        'ParkingTransit',
        'ParkingVoucherTemplate',
        'RegistrationParking',
        'RegistrationParkingDay',
        'Company',
        'CompanyUser',
        'CrewQuantityRequest',
        'Department',
        'RegistrationProfile',
        'RegistrationProfileDay',
        'RegistrationSafetyQuestionAnswer',
        'SafetyQuestion',
        'Subcontractor',
        'Permission',
        'PermissionPreset',
        'User',
        'AccessVoucherTemplate',
        'Barcode',
        'EmailTemplate',
        'material_request',
        'parking_voucher',
        'registration',
        'transport_request',
        'transport_voucher',
        'wristband',
    ];

    const listViewConf = {
        dataSet: activity,
        dataMeta: activityMeta,
        columns: [
            {
                key: "createdAt",
                value: "Time",
                render: item => formatUTCDate(item.createdAt, "DD/MM/YYYY HH:mm:ss")
            },
            {
                key: "causerIdentifier",
                value: "Causer",
            },
            {
                key: "description",
                value: "Description"
            },
            {
                key: "subjectIdentifier",
                value: "Subject",
            },
            {
                key: "properties",
                value: "Properties",
                render: item => <ActivityProperties key={item.id} activity={item}/>
            },
        ],
        baseFilters,
        filters: [
            {
                label: "Subject",
                icon: "la-search",
                type: "select",
                dataset: subjects.sort().map(subject => ({ key: subject, value: subject })),
                key: "subject_type",
            },
            {
                label: "Caused by user",
                icon: "la-user",
                type: "typeahead",
                options: value =>
                    getUsers({
                        "filter[name]": value,
                    }),
                key: "causer_id",
                labelKey: item => `${item.firstName} ${item.lastName}`
            },
            {
                label: "Affected user",
                icon: "la-user",
                type: "typeahead",
                options: value =>
                    getUsers({
                        "filter[name]": value,
                    }),
                key: "subject_id",
                labelKey: item => `${item.firstName} ${item.lastName}`
            },
        ],
        customRowRenderer: activity => (
            <tr key={"activity" + activity.id}>
                <td>{formatUTCDate(activity.createdAt, "DD/MM/YYYY HH:mm:ss")}</td>
                <td>
                    {activity.causerIdentifier === 'System' ? <span className="m--font-bold">CAS</span> : (
                        <Link to={`/admin/employees/${activity.causerId}/edit`} className="m-link m--font-bold">
                            {activity.causerIdentifier}
                        </Link>
                    )}
                </td>
                <td>
                    {renderDescription(activity)}
                </td>
                <td>{activity.subjectTypeString}</td>
                <td>{<ActivityProperties activity={activity}/>}</td>
                <td className="text-right" style={{whiteSpace: "nowrap"}}>
                </td>
            </tr>
        ),
        actions: {
            deleteLink: false,
            editLink: false,
            detailsLink: false
        },
        url: "/admin/activity",
    };

    return (
        <>
            <AdminSubHeader title="Activity" breadcrumbs={[]}/>
            <div className="m-content">
                <div className="row">
                    <div className="col-xl-12">
                        {loading ? (
                            <span>Loading</span>
                        ) : (
                            <ListView
                                config={listViewConf}
                                handleFilterUpdate={setBaseFilters}
                            />
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};

export default Activity;
